import {
  queriesInternalAccounts,
  queriesPayers,
  queriesPayouts,
  queriesTaxWithholdings
} from "@query";
import { selectorOwnPayoutSettings } from "@selectors/selectorOwnPayoutSettings";
import { useWSMutation, WSMutationConfig } from "../../../../query/helpers";
import { QUERY_USERS_MEMBER_PROFILE } from "../../../../query/users/keys";
import {
  internalAccountsApi,
  payersApi,
  payoutsApi,
  taxWithholdingsApi
} from "@api";
import {
  AcknowledgementName,
  Currency,
  InternalAccount,
  InternalAccountType,
  PaymentMethodType,
  PreferredPayoutSpeed,
  ResponseError
} from "@wingspanhq/wingspan-api-sdk";
import { updateAcknowledgements } from "../../../../shared/utils/updateAcknowledgements";

export const useMutationSubmit = (
  type: InternalAccountType,
  currency: Currency,
  acknowledgements: AcknowledgementName[],
  options?: {
    setFundingSource?: boolean;
    setStandardPayoutDestination?: boolean;
  },
  config?: WSMutationConfig<InternalAccount, ResponseError, unknown>
) => {
  return useWSMutation(
    async () => {
      // 1. Update acknowledgements
      await updateAcknowledgements(acknowledgements);

      // 2. Create internal account
      const internalAccount = await internalAccountsApi.createInternalAccount({
        internalAccountCreate: {
          type,
          currency
        }
      });

      // 3. Set usage of the internal account
      // 3.1 Payroll funding method
      if (options?.setFundingSource) {
        const payerSettings = await payersApi.updateCurrentPayerSettings({
          currentPayerSettingsUpdate: {
            payrollSettings: {
              defaultFundingSource: {
                type: PaymentMethodType.INTERNAL_ACCOUNT,
                id: internalAccount.id
              }
            }
          }
        });

        queriesPayers.getCurrentPayerSettings.setQueryData(
          undefined,
          payerSettings
        );
      }

      // 3.2 Payout method
      if (options?.setStandardPayoutDestination) {
        const payoutSettingsList =
          await queriesPayouts.listPayoutSettings.fetchQueryData();
        const payoutSettingsId =
          selectorOwnPayoutSettings(payoutSettingsList)?.id;

        if (payoutSettingsId) {
          await payoutsApi.updatePayoutSettings({
            payoutSettingsId,
            payoutSettingsUpdate: {
              preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
              standardDestination: null
            }
          });
        }
      }

      // Enable withholdings only after the account is created, so the settings
      // are never enabled without an account present.
      if (type === InternalAccountType.TAX_WITHHOLDING) {
        await taxWithholdingsApi.updateTaxWithholdingSettings({
          taxWithholdingSettingsUpdate: { isEnabled: true }
        });
      }

      return internalAccount;
    },
    {
      dependencies: [
        queriesPayers.getCurrentPayerSettings.queryKeyBase,
        queriesPayouts.listPayoutSettings.queryKeyBase,
        queriesPayouts.getPayoutSettings.queryKeyBase,
        queriesInternalAccounts.getInternalAccount.queryKeyBase,
        queriesInternalAccounts.listInternalAccounts.queryKeyBase,
        queriesTaxWithholdings.getTaxWithholdingSettings.queryKeyBase,
        QUERY_USERS_MEMBER_PROFILE
      ],
      ...config
    }
  );
};
